import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Banknote,
  Boxes,
  Calculator,
  Check,
  CheckCircle2,
  Coins,
  DollarSign,
  Download,
  Handshake,
  Hourglass,
  Landmark,
  Link2,
  PiggyBank,
  Receipt,
  RotateCcw,
  ScrollText,
  Tag,
  TrendingUp,
  Wallet,
  Wrench,
  X,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { orgScopedPath } from "@/lib/orgDoor";
import { cn } from "@/lib/utils";
import { useOrgDoor } from "@/hooks/useOrgDoor";
import {
  EBITDA_BASIS_THRESHOLD,
  MAX_DOLLARS,
  MAX_MULTIPLE,
  MIN_MULTIPLE,
  VALUATION_DEFAULTS,
  computeValuation,
  hasPreset,
  outsidePreset,
  resolveBasis,
  toSbaHandoffQuery,
  toValuationQuery,
  valuationCsv,
  type BasisChoice,
  type IndustryMultiples,
  type ValuationInputs,
} from "@/lib/valuationMath";

type NumKey = Exclude<keyof ValuationInputs, "basis">;

interface FieldDef {
  key: NumKey;
  label: string;
  hint: string;
  icon: typeof DollarSign;
  kind: "money" | "multiple";
  min: number;
  max: number;
  /** Blank means 0 / "not entered" instead of a "Required" error. */
  optional?: boolean;
  allowNegative?: boolean;
}

const money = (key: NumKey, label: string, hint: string, icon: typeof DollarSign, extra?: Partial<FieldDef>): FieldDef => ({
  key, label, hint, icon, kind: "money", min: 0, max: MAX_DOLLARS, ...extra,
});
const multiple = (key: NumKey, label: string, hint: string): FieldDef => ({
  key, label, hint, icon: Coins, kind: "multiple", min: MIN_MULTIPLE, max: MAX_MULTIPLE,
});

const F: Record<NumKey, FieldDef> = {
  revenue: money("revenue", "Annual Revenue", "Gross sales for the last full year", TrendingUp),
  netProfit: money("netProfit", "Net Profit (pre-tax)", "Bottom line before tax, as reported", DollarSign, { min: -MAX_DOLLARS, allowNegative: true }),
  ownerComp: money("ownerComp", "One Owner's Salary & Benefits", "ONE working owner only — a partner's pay stays an expense", Wallet),
  depreciation: money("depreciation", "Depreciation & Amortization", "Non-cash charges added back", Receipt),
  interest: money("interest", "Interest Expense", "Financing cost the buyer won't inherit", Landmark),
  oneTime: money("oneTime", "One-time Expenses", "Truly non-recurring costs (legal, move, repairs)", ScrollText),
  sdeMultipleLow: multiple("sdeMultipleLow", "SDE Multiple — Low", "Conservative price ÷ SDE"),
  sdeMultipleHigh: multiple("sdeMultipleHigh", "SDE Multiple — High", "Optimistic price ÷ SDE"),
  ebitdaMultipleLow: multiple("ebitdaMultipleLow", "EBITDA Multiple — Low", "Conservative price ÷ adjusted EBITDA"),
  ebitdaMultipleHigh: multiple("ebitdaMultipleHigh", "EBITDA Multiple — High", "Optimistic price ÷ adjusted EBITDA"),
  inventory: money("inventory", "Inventory", "Saleable stock — added on top of the multiple", Boxes),
  equipment: money("equipment", "Equipment (FF&E)", "Already inside the multiple — sets the asset floor only", Wrench),
  workingCapital: money("workingCapital", "Working Capital Included", "Cash / receivables the seller leaves behind", PiggyBank),
  debt: money("debt", "Debt to Settle at Close", "Seller loans repaid from proceeds", Banknote),
  askingPrice: money("askingPrice", "Asking Price", "Optional — see whether the ask is fair", Tag, { optional: true }),
  sellerNote: money("sellerNote", "Seller Note", "Part of the price the seller finances", Handshake, { optional: true }),
  earnout: money("earnout", "Earnout", "Part of the price paid later, on performance", Hourglass, { optional: true }),
};

const NUM_KEYS = Object.keys(F) as NumKey[];
const STORAGE_KEY = "valuation-calculator:v1";

const usd = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const x = (n: number) => `${n.toFixed(2).replace(/\.?0+$/, "")}x`;

/** "1234567.5" → "1,234,567.5" — display only; the state keeps the raw number. */
function groupDigits(raw: string): string {
  const m = /^(-?)(\d*)(\.\d*)?$/.exec(raw);
  if (!m) return raw;
  return m[1] + m[2].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (m[3] ?? "");
}

function toStrings(v: ValuationInputs): Record<NumKey, string> {
  return Object.fromEntries(
    NUM_KEYS.map((k) => [k, F[k].optional && v[k] === 0 ? "" : String(v[k])]),
  ) as Record<NumKey, string>;
}

function readStored(): { values: Partial<ValuationInputs>; industryId: number | null } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const toneClasses: Record<string, string> = {
  excellent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  good: "bg-sky-100 text-sky-700 border-sky-200",
  ok: "bg-amber-100 text-amber-700 border-amber-200",
  risk: "bg-rose-100 text-rose-700 border-rose-200",
};

interface ValuationCalculatorFormProps {
  /** Pre-fill any input (e.g. from a listing or a shared link). Omitted fields use defaults. */
  initialValues?: Partial<ValuationInputs>;
  /** Pre-select an industry; its multiples are applied unless initialValues sets them. */
  initialIndustryId?: number | null;
  /**
   * Remember the inputs in this browser between visits (standalone page only —
   * a listing's tab must always open on that listing's numbers).
   */
  persist?: boolean;
  /** Shown above the inputs, e.g. how a listing's figures were mapped in. */
  note?: ReactNode;
}

export default function ValuationCalculatorForm({ initialValues, initialIndustryId, persist, note }: ValuationCalculatorFormProps) {
  const navigate = useNavigate();
  const { slug } = useOrgDoor();

  // Precedence: explicit initial values (shared link / listing) > what this
  // browser saved last time > defaults.
  const [boot] = useState(() => {
    const hasExplicit = !!initialValues && Object.keys(initialValues).length > 0;
    const stored = persist && !hasExplicit && !initialIndustryId ? readStored() : null;
    const merged: ValuationInputs = { ...VALUATION_DEFAULTS, ...(stored?.values ?? {}), ...initialValues };
    return { merged, industryId: initialIndustryId ?? stored?.industryId ?? null };
  });

  const [values, setValues] = useState<Record<NumKey, string>>(() => toStrings(boot.merged));
  const [basisChoice, setBasisChoice] = useState<BasisChoice>(boot.merged.basis);
  const [industryId, setIndustryId] = useState<number | null>(boot.industryId);
  const [industries, setIndustries] = useState<IndustryMultiples[]>([]);
  const [focused, setFocused] = useState<NumKey | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get<IndustryMultiples[]>("/lookups/categories")
      .then((cats) => {
        if (!alive) return;
        setIndustries(cats);
        // An industry handed in without explicit multiples applies its preset once.
        const multiplesGiven = initialValues && ("sdeMultipleLow" in initialValues || "ebitdaMultipleLow" in initialValues);
        if (initialIndustryId && !multiplesGiven) {
          const c = cats.find((k) => k.id === initialIndustryId);
          if (c) setValues((v) => ({ ...v, ...presetValues(c) }));
        }
      })
      .catch(() => {});
    return () => { alive = false; };
    // Mount-only: the initial props are read once, like the state above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const industry = industries.find((c) => c.id === industryId) ?? null;

  const setField = (key: NumKey, raw: string) => {
    const f = F[key];
    let clean = raw.replace(/,/g, "").replace(/\s/g, "");
    clean = f.allowNegative ? clean.replace(/(?!^)-/g, "").replace(/[^\d.-]/g, "") : clean.replace(/[^\d.]/g, "");
    setValues((v) => ({ ...v, [key]: clean }));
  };

  const pickIndustry = (id: number | null) => {
    setIndustryId(id);
    const c = industries.find((k) => k.id === id);
    if (c) setValues((v) => ({ ...v, ...presetValues(c) }));
  };

  const errors = useMemo(() => {
    const e: Partial<Record<NumKey, string>> = {};
    for (const f of Object.values(F)) {
      const raw = values[f.key].trim();
      if (raw === "") {
        if (!f.optional) e[f.key] = "Required";
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        e[f.key] = "Enter a number";
        continue;
      }
      if (n < f.min) e[f.key] = `Must be ≥ ${f.min.toLocaleString()}`;
      else if (n > f.max) e[f.key] = `Must be ≤ ${f.max.toLocaleString()}`;
    }
    if (!e.revenue && Number(values.revenue) <= 0) e.revenue = "Must be greater than 0";
    // A low multiple above the high one would print a range that reads backwards.
    for (const [lo, hi] of [["sdeMultipleLow", "sdeMultipleHigh"], ["ebitdaMultipleLow", "ebitdaMultipleHigh"]] as const) {
      if (!e[lo] && !e[hi] && Number(values[lo]) > Number(values[hi])) e[hi] = "High multiple must be ≥ the low multiple";
    }
    return e;
  }, [values]);

  // The basis can depend on SDE, which is only meaningful once the earnings
  // inputs parse — fall back to 0 for any still being typed.
  const liveSde = (["netProfit", "ownerComp", "depreciation", "interest", "oneTime"] as const)
    .reduce((sum, k) => sum + (Number(values[k]) || 0), 0);
  const activeBasis = resolveBasis(basisChoice, liveSde);

  // Errors on the multiples for the basis NOT in use shouldn't block the answer.
  const blockingErrors = Object.keys(errors).filter((k) =>
    activeBasis === "sde" ? !k.startsWith("ebitdaMultiple") : !k.startsWith("sdeMultiple"),
  );

  const inputs = useMemo<ValuationInputs | null>(() => {
    if (blockingErrors.length > 0) return null;
    const nums = Object.fromEntries(NUM_KEYS.map((k) => [k, Number(values[k]) || 0])) as Record<NumKey, number>;
    return { ...nums, basis: basisChoice };
  }, [blockingErrors.length, values, basisChoice]);

  const result = useMemo(() => {
    if (!inputs) return null;
    const r = computeValuation(inputs);
    if (industry && outsidePreset(industry, r.basis, r.multipleLow, r.multipleHigh)) {
      r.warnings.push(`Your ${r.basis.toUpperCase()} multiples are outside the typical ${industry.name} range — be ready to justify them`);
    }
    return r;
  }, [inputs, industry]);

  useEffect(() => {
    if (!persist || !inputs) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ values: inputs, industryId }));
    } catch {
      /* private mode / storage blocked — the calculator works without it */
    }
  }, [persist, inputs, industryId]);

  const reset = () => {
    setValues(toStrings(VALUATION_DEFAULTS));
    setBasisChoice(VALUATION_DEFAULTS.basis);
    setIndustryId(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  const copyLink = async () => {
    if (!inputs) return;
    const url = `${window.location.origin}${orgScopedPath("/buyer/valuation-calculator", slug)}?${toValuationQuery(inputs, industryId)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const downloadCsv = () => {
    if (!inputs || !result) return;
    const blob = new Blob([valuationCsv(inputs, result, industry?.name)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "business-valuation.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const financeDeal = () => {
    if (!inputs || !result) return;
    navigate(`${orgScopedPath("/buyer/sba-calculator", slug)}?${toSbaHandoffQuery(inputs, result)}`);
  };

  const renderField = (f: FieldDef) => {
    const Icon = f.icon;
    const err = errors[f.key];
    const raw = values[f.key];
    const display = f.kind === "money" && focused !== f.key ? groupDigits(raw) : raw;
    return (
      <div key={f.key}>
        <Label htmlFor={f.key} className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {f.label}
        </Label>
        <div className="relative">
          {f.kind === "money" && (
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
              $
            </span>
          )}
          <Input
            id={f.key}
            type="text"
            // iOS's decimal pad has no minus key, so the one field that can go negative gets the full keyboard.
            inputMode={f.allowNegative ? "text" : "decimal"}
            autoComplete="off"
            placeholder={f.optional ? "Not entered" : undefined}
            value={display}
            onFocus={() => setFocused(f.key)}
            onBlur={() => setFocused(null)}
            onChange={(e) => setField(f.key, e.target.value)}
            className={cn(
              "tabular-nums",
              f.kind === "money" && "pl-8",
              f.kind === "multiple" && "pr-12",
              err && "border-rose-400/70 focus-visible:ring-rose-300/40",
            )}
            aria-invalid={!!err}
          />
          {f.kind === "multiple" && (
            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
              x
            </span>
          )}
        </div>
        <p className={cn("mt-1 text-xs", err ? "text-rose-600" : "text-muted-foreground")}>{err ?? f.hint}</p>
      </div>
    );
  };

  const presetIndustries = industries.filter(hasPreset);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
      {/* ── Inputs ─────────────────────────────────────────────── */}
      <Card className="rounded-3xl">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-lg">Business inputs</CardTitle>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:bg-muted"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </CardHeader>
        <CardContent className="space-y-6">
          {note && (
            <div className="rounded-2xl border border-primary/25 bg-primary/10 px-4 py-3 text-xs leading-5 text-foreground/85">{note}</div>
          )}

          <Section title="Financials">
            {renderField(F.revenue)}
            {renderField(F.netProfit)}
          </Section>

          <Section title="Add-backs">
            {renderField(F.ownerComp)}
            {renderField(F.depreciation)}
            {renderField(F.interest)}
            {renderField(F.oneTime)}
          </Section>

          <Section title="Valuation">
            <div>
              <Label htmlFor="industry" className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                Industry
              </Label>
              <select
                id="industry"
                value={industryId ?? ""}
                onChange={(e) => pickIndustry(e.target.value ? Number(e.target.value) : null)}
                className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
              >
                <option value="">Custom multiples</option>
                {presetIndustries.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {industry && hasPreset(industry)
                  ? `Typical ${industry.name}: ${presetLine(industry)} — filled in below, still editable`
                  : "Pick an industry to fill in its typical multiples"}
              </p>
            </div>

            <div>
              <div className="mb-1.5 text-sm font-semibold text-foreground">Valuation basis</div>
              <div className="inline-flex rounded-xl border border-border/70 p-0.5">
                {(["auto", "sde", "ebitda"] as const).map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setBasisChoice(b)}
                    className={cn(
                      "rounded-lg px-3 py-1.5 text-xs font-semibold transition",
                      basisChoice === b ? "bg-gold-gradient text-primary-foreground" : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {b === "auto" ? "Auto" : b.toUpperCase()}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {basisChoice === "auto"
                  ? `Auto: SDE under ${usd(EBITDA_BASIS_THRESHOLD)}, EBITDA above — currently ${activeBasis.toUpperCase()}`
                  : activeBasis === "sde"
                    ? "Owner-operated: price on SDE"
                    : "Manager-run / larger deal: price on adjusted EBITDA"}
              </p>
            </div>

            {activeBasis === "sde" ? (
              <>
                {renderField(F.sdeMultipleLow)}
                {renderField(F.sdeMultipleHigh)}
              </>
            ) : (
              <>
                {renderField(F.ebitdaMultipleLow)}
                {renderField(F.ebitdaMultipleHigh)}
              </>
            )}
            {renderField(F.inventory)}
            {renderField(F.equipment)}
            {renderField(F.workingCapital)}
          </Section>

          <Section title="Deal">
            {renderField(F.askingPrice)}
            {renderField(F.debt)}
            {renderField(F.sellerNote)}
            {renderField(F.earnout)}
          </Section>
        </CardContent>
      </Card>

      {/* ── Results ────────────────────────────────────────────── */}
      <div className="space-y-6">
        {/* Headline valuation range */}
        <Card className="rounded-3xl">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-lg">Estimated value</CardTitle>
            {result && (
              <span className="rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                {result.assetsOnly
                  ? "Asset value only"
                  : `${result.basis.toUpperCase()} × ${x(result.multipleLow)}–${x(result.multipleHigh)}`}
              </span>
            )}
          </CardHeader>
          <CardContent>
            {!result ? (
              <p className="text-sm text-muted-foreground">Fix the highlighted inputs to see a valuation.</p>
            ) : (
              <>
                <div className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Valuation range
                </div>
                <div className="mt-2 flex flex-wrap items-end gap-x-3 gap-y-1">
                  <span className="text-3xl font-extrabold tracking-tight text-foreground tabular-nums sm:text-4xl">
                    {usd(result.low)}
                  </span>
                  {result.high !== result.low && (
                    <>
                      <span className="pb-1 text-muted-foreground">—</span>
                      <span className="text-3xl font-extrabold tracking-tight text-foreground tabular-nums sm:text-4xl">
                        {usd(result.high)}
                      </span>
                    </>
                  )}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Midpoint <span className="font-semibold text-foreground">{usd(result.mid)}</span>
                </p>
                {inputs && inputs.debt > 0 && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Seller equity after debt{" "}
                    <span className={cn("font-semibold tabular-nums", result.equityLow < 0 ? "text-rose-600" : "text-foreground")}>
                      {usd(result.equityLow)} – {usd(result.equityHigh)}
                    </span>
                  </p>
                )}

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <Metric label="SDE" value={usd(result.sde)} hint="Owner's discretionary earnings" />
                  <Metric label="Adjusted EBITDA" value={usd(result.adjustedEbitda)} hint="Manager-run earnings" />
                  <Metric label="SDE margin" value={`${result.sdeMargin.toFixed(1)}%`} hint="SDE ÷ revenue" />
                  <Metric
                    label="Implied revenue multiple"
                    value={`${result.impliedRevenueMultiple.toFixed(2)}x`}
                    hint="Earnings value ÷ revenue (excl. inventory)"
                  />
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">Earnings quality</span>
                  <span className={cn("rounded-full border px-2.5 py-0.5 text-xs font-bold", toneClasses[result.rating.tone])}>
                    {result.rating.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {result.recurringMargin.toFixed(1)}% recurring margin (one-time add-backs excluded)
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Asking price check */}
        {result && (
          <Card className="rounded-3xl">
            <CardHeader>
              <CardTitle className="text-lg">Is the asking price fair?</CardTitle>
            </CardHeader>
            <CardContent>
              {!result.ask ? (
                <p className="text-sm text-muted-foreground">
                  Enter the seller's asking price under <span className="font-semibold text-foreground">Deal</span> to see
                  where it sits in this range and the multiple it implies.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-2xl font-extrabold tracking-tight text-foreground tabular-nums">{usd(result.ask.askingPrice)}</span>
                    <span
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-xs font-bold",
                        result.ask.position === "above" ? toneClasses.risk : result.ask.position === "below" ? toneClasses.excellent : toneClasses.good,
                      )}
                    >
                      {result.ask.position === "above" ? "Above range" : result.ask.position === "below" ? "Below range" : "Within range"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {Math.abs(result.ask.vsMidPct) < 0.5
                      ? "At the midpoint."
                      : `${Math.abs(result.ask.vsMidPct).toFixed(1)}% ${result.ask.vsMidPct > 0 ? "above" : "below"} the midpoint.`}
                    {result.ask.impliedMultiple !== null && (
                      <>
                        {" "}The ask implies{" "}
                        <span className="font-semibold text-foreground">{x(result.ask.impliedMultiple)} {result.basis.toUpperCase()}</span>
                        {" "}vs your {x(result.multipleLow)}–{x(result.multipleHigh)}.
                      </>
                    )}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Deal structure */}
        {result && (
          <Card className="rounded-3xl">
            <CardHeader>
              <CardTitle className="text-lg">Deal structure</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <Row label={result.structure.priceIsAsk ? "Price (asking)" : "Price (midpoint)"} value={usd(result.structure.price)} />
                <Row label="− Seller note" value={usd(result.structure.sellerNote)} muted />
                <Row label="− Earnout" value={usd(result.structure.earnout)} muted />
                <Row label="Cash due at close" value={usd(result.structure.cashAtClose)} strong />
                <Row label="− Debt settled" value={usd(inputs?.debt ?? 0)} muted />
                <Row
                  label="Seller nets at close"
                  value={usd(result.structure.sellerNetAtClose)}
                  strong
                  negative={result.structure.sellerNetAtClose < 0}
                />
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={financeDeal}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-gold-gradient px-4 py-2 text-xs font-bold text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
                >
                  <Calculator className="h-3.5 w-3.5" /> Finance this deal (SBA)
                </button>
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-primary/25 bg-primary/10 px-3 py-2 text-xs font-semibold text-primary transition hover:bg-primary/15"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                  {copied ? "Link copied" : "Copy link"}
                </button>
                <button
                  type="button"
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-border/70 px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:bg-muted"
                >
                  <Download className="h-3.5 w-3.5" /> Download CSV
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sanity warnings — a number can be arithmetically fine and still wrong */}
        <div
          className={cn(
            "flex items-start gap-3 rounded-3xl border p-5",
            !result
              ? "border-border/60 bg-card/60"
              : result.warnings.length === 0
                ? "border-emerald-200 bg-emerald-50"
                : "border-amber-200 bg-amber-50",
          )}
        >
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
              !result
                ? "bg-muted text-muted-foreground"
                : result.warnings.length === 0
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700",
            )}
          >
            {!result ? <X className="h-5 w-5" /> : result.warnings.length === 0 ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground">
              {!result
                ? "Awaiting valid inputs"
                : result.warnings.length === 0
                  ? "Inputs look consistent"
                  : "Check these before relying on the number"}
            </div>
            {result && result.warnings.length > 0 ? (
              <ul className="mt-1.5 space-y-1 text-xs leading-5 text-foreground/80">
                {result.warnings.map((w) => (
                  <li key={w}>• {w}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                An estimate based on an earnings multiple — not a formal appraisal. Real offers move
                with customer concentration, owner dependence, lease terms and clean books.
              </p>
            )}
          </div>
        </div>

        {/* How the number is built — the method matters as much as the output */}
        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="text-lg">How this is calculated</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm leading-6 text-muted-foreground">
            <p>
              <span className="font-semibold text-foreground">SDE</span> = net profit + one working owner's
              pay + depreciation &amp; amortization + interest + one-time expenses.
            </p>
            <p>
              <span className="font-semibold text-foreground">Adjusted EBITDA</span> = the same add-backs
              except owner pay, which stays as the cost of a hired manager.
            </p>
            <p>
              <span className="font-semibold text-foreground">Value</span> = earnings × multiple (SDE under{" "}
              {usd(EBITDA_BASIS_THRESHOLD)} of SDE, adjusted EBITDA above), plus inventory and any working
              capital included. Equipment is already inside the multiple, so it isn't added again. The value
              never drops below inventory + equipment.
            </p>
            <p>
              <span className="font-semibold text-foreground">Seller equity</span> = value − debt settled at
              close. <span className="font-semibold text-foreground">Cash at close</span> = price − seller
              note − earnout.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function presetValues(c: IndustryMultiples): Partial<Record<NumKey, string>> {
  const out: Partial<Record<NumKey, string>> = {};
  if (c.sde_multiple_low != null && c.sde_multiple_high != null) {
    out.sdeMultipleLow = String(c.sde_multiple_low);
    out.sdeMultipleHigh = String(c.sde_multiple_high);
  }
  if (c.ebitda_multiple_low != null && c.ebitda_multiple_high != null) {
    out.ebitdaMultipleLow = String(c.ebitda_multiple_low);
    out.ebitdaMultipleHigh = String(c.ebitda_multiple_high);
  }
  return out;
}

function presetLine(c: IndustryMultiples): string {
  const parts: string[] = [];
  if (c.sde_multiple_low != null && c.sde_multiple_high != null) parts.push(`SDE ${x(c.sde_multiple_low)}–${x(c.sde_multiple_high)}`);
  if (c.ebitda_multiple_low != null && c.ebitda_multiple_high != null) parts.push(`EBITDA ${x(c.ebitda_multiple_low)}–${x(c.ebitda_multiple_high)}`);
  return parts.join(" · ");
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-4">
      <div className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-primary">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, muted, strong, negative }: { label: string; value: string; muted?: boolean; strong?: boolean; negative?: boolean }) {
  return (
    <div className={cn("flex items-center justify-between gap-3", strong && "border-t border-border/60 pt-2")}>
      <span className={cn(muted ? "text-muted-foreground" : "text-foreground", strong && "font-semibold")}>{label}</span>
      <span className={cn("tabular-nums", strong ? "font-bold" : "", negative ? "text-rose-600" : muted ? "text-muted-foreground" : "text-foreground")}>
        {value}
      </span>
    </div>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/70 p-4">
      <div className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="mt-1.5 text-xl font-extrabold tracking-tight text-foreground tabular-nums">{value}</div>
      <div className="mt-1 text-[0.7rem] leading-4 text-muted-foreground">{hint}</div>
    </div>
  );
}
